import { IonItem } from '@ionic/react';
import { DateTime } from 'luxon';
import React, { useEffect, useState } from 'react';
import { NavLink } from 'react-router-dom';
import { useSearchAdminActionsQuery } from '../api/AdminActionApi';
import { useUserHasRole } from '../hooks/useUserHasRole';
import {
	IconBusiness,
	IconCalendar,
	IconFeedback,
	IconLogout,
	IconProfile,
	IconReport,
	IconShield,
	IconSiteSettings,
	IconTopics
} from './QuirkIcons';
import { ReleaseVersionRefresh } from './ReleaseVersionRefresh';
import styles from './SettingsMenuDesktop.module.css';
import { Share } from './Share';
import { SiteIcon } from './SiteIcon';
import { AdminHide } from './admin/AdminHide';
import { AnonymousOnly } from '../pages/admin/AnonymousOnly';
import { RealUserOnly } from '../pages/admin/RealUserOnly';
import { Badge } from './Badge.js';
export const SettingsMenuDesktop: React.FC = () => {
	const [dateRange, setDateRange] = useState<string[]>([]);

	const admin = useUserHasRole('admin');

	const { data: reports } = useSearchAdminActionsQuery(
		{
			startDate: dateRange[0],
			endDate: dateRange[1],
			actionTaken: 'created'
		},
		{ skip: !admin }
	);

	useEffect(() => {
		if (dateRange.length === 0) {
			setDateRange([
				DateTime.now().minus({ days: 7 }).toUTC().toJSDate().toISOString(),
				DateTime.now().toUTC().toJSDate().toISOString()
			]);
		}
	}, [dateRange]);

	return (
		<div className={styles.menuContainer}>
			<NavLink to="/main/settings/about/" className="nounderline" data-testid="desktopAboutButton">
				<IonItem className={styles.ionItem} lines="none">
					<div className={styles.menuItemContainer}>
						<div className={styles.siteIconContainer}>
							<SiteIcon rounded={true} />
						</div>
						<div className={styles.menuItemLabel}>About</div>
					</div>
				</IonItem>
			</NavLink>
			<IonItem className={styles.ionItem} lines="none" data-testid="desktopShareButton">
				<Share label="Invite a friend" gutter />
			</IonItem>
			<NavLink to="/main/bus/" className="nounderline" data-testid="desktopBusinessesButton">
				<IonItem className={styles.ionItem} lines="none">
					<div className={styles.menuItemContainer}>
						<IconBusiness />
						<div className={styles.menuItemLabel}>Local Businesses</div>
					</div>
				</IonItem>
			</NavLink>
			<NavLink to="/main/events/" className="nounderline" data-testid="desktopEventsButton">
				<IonItem className={styles.ionItem} lines="none">
					<div className={styles.menuItemContainer}>
						<IconCalendar />
						<div className={styles.menuItemLabel}>Upcoming Events</div>
					</div>
				</IonItem>
			</NavLink>
			<RealUserOnly>
				<NavLink
					to={`/main/settings/profile/`}
					className="nounderline"
					data-testid="desktopProfileSettingsButton"
				>
					<IonItem className={styles.ionItem} lines="none">
						<div className={styles.menuItemContainer}>
							<IconProfile />
							<div className={styles.menuItemLabel}>Profile Settings</div>
						</div>
					</IonItem>
				</NavLink>
				<NavLink to={`/main/settings/topics/`} className="nounderline" data-testid="desktopTopicsButton">
					<IonItem className={styles.ionItem} lines="none">
						<div className={styles.menuItemContainer}>
							<IconTopics />
							<div className={styles.menuItemLabel}>My Topics</div>
						</div>
					</IonItem>
				</NavLink>
				<AdminHide>
					<NavLink to={`/main/settings/admin/users`} className="nounderline">
						<IonItem className={styles.ionItem} lines="none">
							<div className={styles.menuItemContainer}>
								<IconShield />
								<div className={styles.menuItemLabel}>Admin User Settings</div>
							</div>
						</IonItem>
					</NavLink>
				</AdminHide>
				<AdminHide>
					<NavLink to={`/main/settings/admin/siteSettings`} className="nounderline">
						<IonItem className={styles.ionItem} lines="none">
							<div className={styles.menuItemContainer}>
								<IconSiteSettings />
								<div className={styles.menuItemLabel}>Site Settings</div>
							</div>
						</IonItem>
					</NavLink>
				</AdminHide>
				<AdminHide>
					<NavLink
						to={`/main/settings/admin/reports`}
						className="nounderline"
						data-testid="ReviewReportsButton"
					>
						<IonItem className={styles.ionItem} lines="none">
							<div className={styles.menuItemContainer}>
								<IconReport />
								<div className={styles.menuItemLabelWithJewel}>
									<div className={styles.menuItemLabel}>Review Reports</div>
									{reports && (
										<Badge
											content={reports.length}
											small
											type={reports.length > 0 ? 'alert' : 'info'}
										/>
									)}
								</div>
							</div>
						</IonItem>
					</NavLink>
				</AdminHide>

				<NavLink
					to="/main/settings/about/sendfeedback/"
					className="nounderline"
					data-testid="desktopSubmitFeedbackButton"
				>
					<IonItem className={styles.ionItem} lines="none">
						<div className={styles.menuItemContainer}>
							<IconFeedback />
							<div className={styles.menuItemLabel}>Submit Feedback</div>
						</div>
					</IonItem>
				</NavLink>

				<NavLink to={`/main/settings/logout`} className="nounderline" data-testid="desktopLogoutButton">
					<IonItem className={styles.ionItem} lines="none">
						<div className={styles.menuItemContainer}>
							<IconLogout />
							<div className={styles.menuItemLabel}>Logout</div>
						</div>
					</IonItem>
				</NavLink>
			</RealUserOnly>
			<AnonymousOnly>
				<NavLink to={`/main/settings/login`} className="nounderline" data-testid="desktopLoginButton">
					<IonItem className={styles.ionItem} lines="none">
						<div className={styles.menuItemContainer}>
							<IconShield /> <div className={styles.menuItemLabel}>Login</div>
						</div>
					</IonItem>
				</NavLink>
			</AnonymousOnly>
			<ReleaseVersionRefresh />
		</div>
	);
};
